/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
import { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useParams } from "react-router-dom";
import parse from "html-react-parser";

// components imports
import { Button, Card, Tooltip, Typography } from "antd";
import { CheckCircleOutlined, CheckOutlined, CloseCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";

// misc imports
import { ICurrency, IPendingSubscriptionAction, ISubscriptionPlan } from "@/interfaces/SubscriptionPlans";
import { useSetSubscription, useUpdateSubscription } from "@/api/subscriptions";
import replaceUrlParams from "@/utils/string/replaceUrlParams";
import { CURRENCY_SYMBOLS, NAV_CHECKOUT } from "@/constants";
import { numberToFixed } from "@/utils/number/numberToFixed";
import { useServiceTypes } from "@/api/services";
import { SASS_VARS } from "@/utils/css";
import { useCancelSubscription } from "@/api/subscriptions/useCancelSubscription";
import { useGetProject } from "@/api/projects";
import { getFormattedDate } from "@/utils/date/getFormattedDate";
import { tokens as Tokens } from "@/utils/tokens";
import { queryClient } from "@/utils/queryClient";
import { ITokens } from "@/interfaces/User.interface";
import { useUserInfo } from "@/api/user";

const baseClassName = "subscription-plan";
const { Text } = Typography;

export const SubscriptionPlan = ({
  plan,
  isActive,
  isTrial,
  selectedCurrency,
  hasActiveSubscription,
  disableBtn,
  hasBeenCancelled,
  setPendingSubscriptionAction,
}: {
  plan: ISubscriptionPlan;
  isActive: boolean;
  isTrial: boolean;
  selectedCurrency: ICurrency;
  hasActiveSubscription: boolean;
  disableBtn: boolean;
  hasBeenCancelled: boolean;
  setPendingSubscriptionAction: Dispatch<SetStateAction<IPendingSubscriptionAction | null>>;
}): JSX.Element => {
  const { t } = useTranslation();
  const tokensInstance = Tokens;

  const { workspaceId, projectId } = useParams();
  const { data: projectData } = useGetProject({
    workspaceId: workspaceId as string,
    projectId: projectId as string,
  });

  const { data: subscriptionData, mutate: setSubscription } = useSetSubscription();
  const { mutate: updateSubscription, isLoading: isUpdatingSubscription } = useUpdateSubscription();
  const { isLoading: isCancelingSubscription } = useCancelSubscription();
  const { data: serviceTypesData } = useServiceTypes();
  const userAuth: ITokens | undefined = queryClient.getQueryData(["user-auth"]);
  const { access: accessToken } = userAuth || {};
  const { data: userData } = useUserInfo(tokensInstance.getTokenSub(accessToken) as string);
  // If the user has selected a specific plan redirect to checkout
  if (subscriptionData) {
    return (
      <Navigate
        state={subscriptionData}
        to={replaceUrlParams(NAV_CHECKOUT, {
          workspaceId: workspaceId as string,
          projectId: projectId as string,
        })}
      />
    );
  }

  console.log("plan: ", plan);

  const getPriceObj = () =>
    plan?.prices?.find((priceObj) => priceObj.currency === selectedCurrency) || { id: "", currency: selectedCurrency, unit_amount: 0 };

  const handleSubscription = () => {
    if (hasActiveSubscription && !isTrial) {
      const isDowngrading =
        projectData?.subscription_plan === "Enterprise" || (projectData?.subscription_plan === "Business" && plan.title === "Starter");
      if (isDowngrading) {
        return setPendingSubscriptionAction({
          action: "downgrade",
          metadata: {
            priceId: getPriceObj().id,
          },
        });
      }
      // Update subscription with new price id
      updateSubscription({
        workspaceId: workspaceId as string,
        projectId: projectId as string,
        priceId: getPriceObj().id,
      });
    } else {
      setSubscription({
        workspaceId: workspaceId as string,
        projectId: projectId as string,
        priceId: getPriceObj().id,
        currency: getPriceObj().currency,
      });
    }
  };

  const handleCancelSubscription = () => {
    setPendingSubscriptionAction({ action: "cancel" });
  };

  const renderServiceTypes = () => {
    return (
      <div className="mb-3">
        {serviceTypesData?.map((serviceType) => {
          if (!serviceType.is_active) {
            return;
          }
          return (
            <p className="mb-0" key={`${plan.title}__${serviceType.key}`}>
              {plan.service_types?.includes(serviceType.key) ? (
                <CheckCircleOutlined style={{ color: SASS_VARS.successColor }} />
              ) : (
                <CloseCircleOutlined style={{ color: SASS_VARS.errorColor }} />
              )}{" "}
              <span className={`${baseClassName}__service`}>{serviceType.key}</span>&nbsp;
              <Tooltip title={parse(serviceType.description)}>
                <InfoCircleOutlined style={{ color: SASS_VARS.colorPrimary, fontSize: "0.625rem", verticalAlign: "baseline" }} />
              </Tooltip>
            </p>
          );
        })}
      </div>
    );
  };

  const renderLimits = () => {
    return plan.limits.map((limit: any, index: number) => (
      <div key={`${plan.title}__${index}`}>
        <p className="mb-0">
          <strong>{limit.requests_per_minute}</strong> {t("subscription_plans.requests_per_minute")}
        </p>
        <p className="mb-0">
          <strong>{limit.service_specific_limits.max_num_of_inputs}</strong> {t("subscription_plans.max_number_of_inputs")}
        </p>
      </div>
    ));
  };

  const renderPlanButton = () => {
    if (plan.is_default || (isActive && !isTrial && hasBeenCancelled)) {
      return;
    }

    if (isActive && !isTrial) {
      return (
        <Button
          onClick={() => {
            handleCancelSubscription();
          }}
          loading={isCancelingSubscription}
          disabled={disableBtn}
          type="default"
          danger
        >
          {t("subscription_plans.cancel_subscription")}
        </Button>
      );
    }

    return (
      <div>
        {isTrial && isActive ? (
          <p style={{ lineHeight: 1 }}>
            <Text type="success" className={`${baseClassName}__header__title_trial`}>
              {t("project_single.in_trial_ext", {
                date: getFormattedDate(projectData?.pending_subscription_cancellation_date, userData?.timezone, "DD-MM-YYYY"),
              })}
            </Text>
          </p>
        ) : null}
        <Button
          onClick={() => {
            handleSubscription();
          }}
          type="primary"
          disabled={disableBtn}
          loading={isUpdatingSubscription}
        >
          {t("subscription_plans.subscribe")}
        </Button>
      </div>
    );
  };

  return (
    <>
      <Card className={`${baseClassName} h-100 ${isActive ? "active" : ""}`} styles={{ body: { height: "100%" } }}>
        {plan.is_featured && projectData?.subscription_plan === "Free" && !isTrial ? (
          <div className={`${baseClassName}__ribbon`}>{t("subscription_plans.featured")}</div>
        ) : null}
        {isActive ? (
          <div className={`${baseClassName}__active-indicator`}>
            <CheckOutlined />
          </div>
        ) : null}
        <div className={`${baseClassName}__inner h-100`}>
          <div className={`${baseClassName}__header`}>
            <h4 className={`${baseClassName}__header__title`}>{plan.title}</h4>
            <h5 className={`${baseClassName}__header__subtitle`}>
              <span className={`${baseClassName}__header__subtitle__currency`}>{parse(CURRENCY_SYMBOLS[selectedCurrency].icon)}</span>
              <span className={`${baseClassName}__header__subtitle__cost`}>{numberToFixed(getPriceObj().unit_amount)}</span>
              <span className={`${baseClassName}__header__subtitle__per`}>/m</span>
            </h5>
          </div>
          <div className={`${baseClassName}__body mb-3`}>
            {renderServiceTypes()}
            {renderLimits()}
          </div>

          {/* Asked to be removed by PO */}
          {/* <div className="mt-3 text-start">{parse(plan.description)}</div> : null */}

          <div className={`${baseClassName}__footer text-center`}>{renderPlanButton()}</div>
        </div>
      </Card>
    </>
  );
};
